import { forgotPassword } from "api/auth";
import { Form, Formik, type FormikHelpers } from "formik";
import AccountsContainer from "pages/accounts-onboarding/components/accounts-container";
import type { ForgotPasswordModel } from "pages/accounts-onboarding/models";
import { createForgotPasswordSchema } from "pages/accounts-onboarding/schema";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ForgotPasswordForm from "./components";
import ResetToast from "./components/ResetToast";

import styles from "./ForgotPassword.module.css";

type ForgotPasswordPageProps = {
	initialValues: ForgotPasswordModel;
};

export const ForgotPasswordPage = ({ initialValues }: ForgotPasswordPageProps) => {
	const [isResetEmailSent, setIsResetEmailSent] = useState(false);
	const { t } = useTranslation("accountsPage");
	const validationSchema = useMemo(() => createForgotPasswordSchema(t), [t]);

	const onFormSubmit = useCallback(
		async (values: ForgotPasswordModel, formikHelpers: FormikHelpers<ForgotPasswordModel>): Promise<void> => {
			try {
				await forgotPassword(values.email);
			} catch {
				// The endpoint intentionally succeeds even for unknown accounts (no account enumeration),
				// so surface the same confirmation regardless of the outcome.
			} finally {
				setIsResetEmailSent(true);
				formikHelpers.setSubmitting(false);
			}
		},
		[]
	);

	const onDismiss = useCallback(() => setIsResetEmailSent(false), []);

	return (
		<Formik initialValues={initialValues} onSubmit={onFormSubmit} validationSchema={validationSchema}>
			<Form className={styles.page} noValidate>
				{isResetEmailSent && <ResetToast onDismiss={onDismiss} />}
				<AccountsContainer>
					<ForgotPasswordForm />
				</AccountsContainer>
			</Form>
		</Formik>
	);
};
